import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, LineChart, Calendar, Check, ShieldCheck, Activity, BarChart, BriefcaseBusiness, Calculator, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, Play, Pause, Square, Eye, AlertCircle } from 'lucide-react';
import { Strategy, Symbol } from './types';
import { MODEL_TYPES } from '@/components/trading/constants';
import { crossValidatePriceSeries, CVMetrics } from '@/services/backtester';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { deploymentService, DeploymentStatus } from '@/services/deploymentService';
import { vmTradingService } from '@/services/vmTradingService';
import { useToast } from '@/components/ui/use-toast';

interface StrategyDetailsDialogProps {
  strategy: Strategy | null;
  onOpenChange: (open: boolean) => void;
}

interface TradeActivity {
  id: string;
  strategy_id: string; 
  symbol: string;
  action: 'buy' | 'sell';
  quantity: number;
  price: number;
  timestamp: string;
  status: 'pending' | 'executed' | 'open' | 'closed' | 'failed';
  order_type?: string;
}

interface LivePositionData {
  symbol: string;
  qty: number;
  current_price: number;
  market_value: number;
  unrealized_pl: number;
  unrealized_plpc: number;
  cost_basis: number;
}



export const StrategyDetailsDialog: React.FC<StrategyDetailsDialogProps> = ({
  strategy,
  onOpenChange,
}) => {
  const { toast } = useToast();
  const renderSymbols = () => {
    if (!strategy?.symbols && (!strategy?.baskets || strategy.baskets.length === 0)) return <span className="text-sm text-gray-500">N/A</span>;

    let symbolList: Symbol[] = [];

    // First, try to extract symbols from baskets
    if (strategy.baskets && Array.isArray(strategy.baskets) && strategy.baskets.length > 0) {
      strategy.baskets.forEach(basket => {
        if (basket.symbols && Array.isArray(basket.symbols)) {
          basket.symbols.forEach(symbol => {
            symbolList.push({
              ticker: symbol.ticker || symbol.symbol || '',
              symbol: symbol.symbol || symbol.ticker || '',
              direction: symbol.direction || 'long',
              weight: symbol.weight || 1
            });
          });
        }
      });
    }

    // If no symbols from baskets, try regular symbols field
    if (symbolList.length === 0 && strategy?.symbols) {
    if (typeof strategy.symbols === 'string') {
      try {
        const symbolsArray = JSON.parse(strategy.symbols);
        if (Array.isArray(symbolsArray)) {
          symbolList = symbolsArray.map(s => ({
            ticker: s.symbol || s.name || '',
            symbol: s.symbol || s.name || '',
            direction: s.direction || 'long',
            weight: s.weight
          }));
        }
      } catch (e) {
        const symbolsString = strategy.symbols || '';
        symbolList = typeof symbolsString === 'string' ? 
          symbolsString.split(',').map(s => ({
            ticker: s.trim(),
            symbol: s.trim(),
            direction: 'long' as const,
            weight: 1
          })) : [];
      }
    } else if (Array.isArray(strategy.symbols)) {
      symbolList = strategy.symbols.map(s => ({
        ticker: s.symbol || s.name || '',
        symbol: s.symbol || s.name || '',
        direction: s.direction || 'long',
        weight: s.weight
      }));
      }
    }

    if (symbolList.length === 0) return <span className="text-sm text-gray-500">N/A</span>;

    return symbolList.map((symbol: Symbol, index: number) => (
      <div
        key={`${symbol.symbol}-${index}`}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          symbol.direction === 'auto'
            ? 'bg-blue-100 text-blue-700'
            : symbol.direction === 'long'
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`}
        title={`Direction: ${symbol.direction}`}
      >
        {symbol.symbol}
        {getDirectionIcon(symbol.direction)}
      </div>
    ));
  };

  const getTimeframeDisplay = (strategy: Strategy | null): string => {
    if (!strategy) return 'N/A';

    // First try to get execution timing from strategy object
    let executionTiming = strategy.executionTiming;
    
    // If not found, try to get it from deployment status (for deployed strategies)
    if (!executionTiming && deploymentStatus?.strategy_config?.executionTiming) {
      executionTiming = deploymentStatus.strategy_config.executionTiming;
      console.log('StrategyDetailsDialog: Using execution timing from deployment status:', executionTiming);
    }

    if (executionTiming) {
      const frequency = executionTiming.signalFrequency;
      const freqMap: { [key: string]: string } = {
        'daily_close': 'Daily (Close)',
        'daily_open': 'Daily (Open)',
        'hourly': 'Hourly',
        '15min': '15 Min',
        '5min': '5 Min',
        '1min': '1 Min',
        'continuous': 'Continuous (HFT)',
        'event_driven': 'Event Driven'
      };
      return freqMap[frequency] || 'Custom';
    }
    return strategy.duration || 'N/A';
  };

  const formatRiskValue = (value: string | undefined, type: 'percentage' | 'atr' | 'volatility' | 'risk_reward' | undefined): string => {
    if (value === undefined || value === null || value === '') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';

    switch(type) {
      case 'percentage': return `${numValue}%`;
      case 'atr': return `${numValue}x ATR`;
      case 'volatility': return `${numValue} Std Dev`;
      case 'risk_reward': return `${numValue}:1 Ratio`;
      default: return value;
    }
  };

  const getEngineDisplay = (strategy: Strategy | null): string => {
    if (!strategy) return "N/A";

    if (strategy.engine && strategy.engine !== 'default') {
      const engineName = MODEL_TYPES.find(mt => mt.id === strategy.engine)?.name;
      return engineName || strategy.engine;
    }

    if (!strategy.models || strategy.models.length === 0) {
      return "Static";
    }

    if (!Array.isArray(strategy.models)) {
      console.warn("Strategy models is not an array:", strategy.models);
      return "Invalid Models";
    }

    return strategy.models
      .map(m => MODEL_TYPES.find(mt => mt.id === m.type)?.name || m.type)
      .join(', ');
  };

  const [cvMetrics, setCvMetrics] = useState<CVMetrics | null>(null);
  const [cvLoading, setCvLoading] = useState<boolean>(false);

  const [activities, setActivities] = useState<TradeActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState<boolean>(true);
  const [activityError, setActivityError] = useState<string | null>(null);

  // Deployment status for deployed strategies
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [isLoadingDeploymentStatus, setIsLoadingDeploymentStatus] = useState(false);

  // Live trading data
  const [livePositions, setLivePositions] = useState<LivePositionData[]>([]);
  const [isLoadingLivePositions, setIsLoadingLivePositions] = useState(false);
  const [livePositionsError, setLivePositionsError] = useState<string | null>(null);



  // Real-time updates
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isLiveUpdatesActive, setIsLiveUpdatesActive] = useState(false);

  const targetSymbolForCV = useMemo(() => {
    if (!strategy?.symbols && (!strategy?.baskets || strategy.baskets.length === 0)) return null;
    let firstSymbolString: string | null = null;

    // DEBUG: Log the strategy object to see what we're working with
    console.log('StrategyDetailsDialog: Full strategy object:', strategy);
    console.log('StrategyDetailsDialog: strategy.symbols:', strategy?.symbols);
    console.log('StrategyDetailsDialog: strategy.baskets:', strategy?.baskets);

    // FALLBACK: If symbols and baskets are empty, try to parse from strategy name
    const hasEmptySymbols = (!strategy?.symbols || (Array.isArray(strategy.symbols) && strategy.symbols.length === 0));
    const hasEmptyBaskets = (!strategy?.baskets || strategy.baskets.length === 0);
    
    if (hasEmptySymbols && hasEmptyBaskets && strategy?.name) {
      console.log('StrategyDetailsDialog: Symbols and baskets are empty, attempting to parse from strategy name:', strategy.name);
      
      // Try to extract symbols from strategy name (e.g., "6/1 PEP KO" -> ["PEP", "KO"])
      const symbolRegex = /\b[A-Z]{1,5}\b/g; // Match 1-5 uppercase letter words (typical stock symbols)
      const potentialSymbols = strategy.name.match(symbolRegex);
      
      if (potentialSymbols && potentialSymbols.length > 0) {
        // Filter out common words that aren't symbols
        const excludeWords = ['THE', 'AND', 'OR', 'AI', 'ML', 'API', 'USA', 'USD', 'EUR', 'GBP'];
        const likelySymbols = potentialSymbols.filter(symbol => 
          !excludeWords.includes(symbol) && 
          symbol.length >= 1 && 
          symbol.length <= 5
        );
        
        if (likelySymbols.length > 0) {
          firstSymbolString = likelySymbols[0];
          console.log('StrategyDetailsDialog: Extracted symbol from name:', firstSymbolString, 'from candidates:', likelySymbols);
          return firstSymbolString.toUpperCase();
        }
      }
    }

    // First, try to get symbol from baskets
    if (strategy.baskets && Array.isArray(strategy.baskets) && strategy.baskets.length > 0) {
      console.log('StrategyDetailsDialog: Found baskets, extracting symbols...');
      const firstBasket = strategy.baskets[0];
      if (firstBasket.symbols && Array.isArray(firstBasket.symbols) && firstBasket.symbols.length > 0) {
        const firstSymbol = firstBasket.symbols[0];
        firstSymbolString = firstSymbol.ticker || firstSymbol.symbol || null;
        console.log('StrategyDetailsDialog: Extracted symbol from basket:', firstSymbolString);
        if (firstSymbolString) {
          return firstSymbolString.toUpperCase();
        }
      }
    }

    // Fallback to regular symbols field
    if (strategy?.symbols) {
      console.log('StrategyDetailsDialog: No baskets found, checking regular symbols field...');
    if (typeof strategy.symbols === 'string') {
      try {
        // Attempt to parse as JSON array of objects like [{symbol: "AAPL"}, ...]
        const symbolsArray = JSON.parse(strategy.symbols);
        if (Array.isArray(symbolsArray) && symbolsArray.length > 0) {
          const firstObj = symbolsArray[0];
          // Check if firstObj is string before trimming
          if (typeof firstObj === 'string') firstSymbolString = firstObj.trim();
          else firstSymbolString = firstObj?.symbol || firstObj?.name || firstObj?.ticker || null;
        } else if (typeof strategy.symbols === 'string' && strategy.symbols.trim() !== ''){
           // Fallback for simple comma-separated string e.g., "AAPL,MSFT"
           const first = strategy.symbols.split(',')[0];
           firstSymbolString = first ? first.trim() : null; // Ensure 'first' exists before trimming
        }
      } catch (e) {
        // If JSON.parse fails, assume it's a simple comma-separated string
        if (typeof strategy.symbols === 'string'){
            const first = strategy.symbols.split(',')[0];
            firstSymbolString = first ? first.trim() : null; // Ensure 'first' exists before trimming
        }
      }
    } else if (Array.isArray(strategy.symbols) && strategy.symbols.length > 0) {
      const firstElement = strategy.symbols[0]; 
      if (typeof firstElement === 'string') {
        const trimmedSymbol = (firstElement as string).trim();
        if (trimmedSymbol) { 
            firstSymbolString = trimmedSymbol;
        }
      } else if (typeof firstElement === 'object' && firstElement !== null) { 
        const potentialSymbol = (firstElement as any).symbol || (firstElement as any).ticker;
        if (typeof potentialSymbol === 'string') {
          firstSymbolString = potentialSymbol;
        }
      }
      // If neither string nor object with symbol/ticker, firstSymbolString remains null
    }
    }
    
    console.log('StrategyDetailsDialog: Final extracted symbol:', firstSymbolString);
    return firstSymbolString ? firstSymbolString.toUpperCase() : null;
  }, [strategy?.symbols, strategy?.baskets, strategy?.name]);

  useEffect(() => {
    if (targetSymbolForCV) {
      setCvLoading(true);
      setCvMetrics(null);
      crossValidatePriceSeries(targetSymbolForCV, 5)
        .then(metrics => {
          setCvMetrics(metrics);
        })
        .catch(err => {
          console.error(`CV error for ${targetSymbolForCV}:`, err);
          setCvMetrics(null); 
        })
        .finally(() => {
          setCvLoading(false);
        });
    } else {
      setCvMetrics(null);
      setCvLoading(false);
    }
  }, [targetSymbolForCV]);

  const fetchStrategyActivity = useCallback(async (strategyIdToFetch: string | undefined) => {
    if (!strategyIdToFetch) {
      setActivities([]);
      setActivityLoading(false);
      return;
    }

    setActivityLoading(true);
    setActivityError(null);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        throw new Error(sessionError?.message || "User not authenticated");
      }
      const userId = session.user.id;

      const { data, error: dbError } = await supabase
        .from('trades')
        .select('*')
        .eq('user_id', userId) 
        .eq('strategy_id', strategyIdToFetch)
        .order('entry_timestamp', { ascending: false }) 
        .limit(5); 

      if (dbError) {
        throw dbError;
      }
      
      const fetchedActivities: TradeActivity[] = (data || []).map((dbTrade: any) => ({
        id: dbTrade.id,
        strategy_id: dbTrade.strategy_id,
        symbol: dbTrade.symbol,
        action: dbTrade.action,
        quantity: dbTrade.quantity,
        price: dbTrade.entry_price, 
        timestamp: dbTrade.entry_timestamp, 
        status: dbTrade.status,
        order_type: dbTrade.order_type || dbTrade.metadata?.order_type,
      }));
      setActivities(fetchedActivities);

    } catch (err: any) {
      console.error('Error fetching strategy activity:', err);
      setActivityError(err.message || 'Failed to fetch recent activity.');
      setActivities([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (strategy?.id) {
      fetchStrategyActivity(strategy.id);
    } else {
      setActivities([]);
      setActivityLoading(false);
      setActivityError(null);
    }
  }, [strategy?.id, fetchStrategyActivity]);

  // Load deployment status for deployed strategies
  const loadDeploymentStatus = useCallback(async (strategyId: string) => {
    try {
      setIsLoadingDeploymentStatus(true);
      
      // Add timeout like StrategyCard to prevent hanging
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      const timeoutMs = isLocalhost ? 3000 : 10000;
      
      const withTimeout = (promise: Promise<any>) => {
        return Promise.race([
          promise,
          new Promise<any>((_, reject) => 
            window.setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };
      
      const status = await withTimeout(deploymentService.getDeploymentStatus(strategyId));
      setDeploymentStatus(status);
      console.log('StrategyDetailsDialog: Raw deployment status received:', status);
      console.log('StrategyDetailsDialog: Strategy config from deployment:', status?.strategy_config);
      console.log('StrategyDetailsDialog: Risk params from deployment:', status?.strategy_config?.risk_params);
      console.log('StrategyDetailsDialog: Trading mode:', status?.trading_mode);
      console.log('StrategyDetailsDialog: Status:', status?.status);
    } catch (error) {
      console.warn('StrategyDetailsDialog: Failed to load deployment status:', error);
      setDeploymentStatus(null);
    } finally {
      setIsLoadingDeploymentStatus(false);
    }
  }, []);

  // Load deployment status when strategy changes
  useEffect(() => {
    if (strategy?.id && (strategy.isActive || strategy.backtestSettings?.mode === 'live' || strategy.backtestSettings?.mode === 'paper')) {
      loadDeploymentStatus(strategy.id);
    } else {
      setDeploymentStatus(null);
    }
  }, [strategy?.id, strategy?.isActive, loadDeploymentStatus]);

     // Fetch live positions for deployed strategies
   const fetchLivePositions = useCallback(async (strategyId: string) => {
     if (!strategyId) {
       setLivePositions([]);
       setIsLoadingLivePositions(false);
       return;
     }

     setIsLoadingLivePositions(true);
     setLivePositionsError(null);

     try {
       // Use existing getPositions method with LIVE mode
       const paperPositions = await vmTradingService.getPositions('PAPER');
       const livePositions = await vmTradingService.getPositions('LIVE');
       const allPositions = [...paperPositions, ...livePositions];
       
       // Transform to our expected format
       const transformedPositions = allPositions.map(pos => ({
         symbol: pos.symbol || 'UNKNOWN',
         qty: parseFloat(pos.qty || pos.quantity || '0'),
         current_price: parseFloat(pos.current_price || '0'),
         market_value: parseFloat(pos.market_value || '0'),
         unrealized_pl: parseFloat(pos.unrealized_pl || '0'),
         unrealized_plpc: parseFloat(pos.unrealized_plpc || '0'),
         cost_basis: parseFloat(pos.cost_basis || '0')
       }));
       
       setLivePositions(transformedPositions);
       console.log('StrategyDetailsDialog: Loaded live positions:', transformedPositions);
     } catch (err: any) {
       console.error('Error fetching live positions:', err);
       setLivePositionsError(err.message || 'Failed to fetch live positions.');
       setLivePositions([]);
     } finally {
       setIsLoadingLivePositions(false);
     }
   }, []);



     // Load live positions when strategy changes
   useEffect(() => {
     if (strategy?.id && (strategy.isActive || strategy.backtestSettings?.mode === 'live' || strategy.backtestSettings?.mode === 'paper')) {
       fetchLivePositions(strategy.id);
     } else {
       setLivePositions([]);
       setIsLoadingLivePositions(false);
       setLivePositionsError(null);
     }
   }, [strategy?.id, strategy?.isActive, fetchLivePositions]);

   // Auto-refresh for live strategies
   useEffect(() => {
     if (!strategy?.id || !strategy.isActive) {
       setIsLiveUpdatesActive(false);
       return;
     }

     setIsLiveUpdatesActive(true);
     const intervalId = setInterval(() => {
       if (strategy.id) {
         fetchLivePositions(strategy.id);
         loadDeploymentStatus(strategy.id);
         setLastUpdate(new Date());
       }
     }, 30000); // Update every 30 seconds for live strategies

     return () => {
       clearInterval(intervalId);
       setIsLiveUpdatesActive(false);
     };
   }, [strategy?.id, strategy?.isActive, fetchLivePositions, loadDeploymentStatus]);

   // Strategy control handlers
   const handleStrategyControl = async (action: 'pause' | 'resume' | 'stop') => {
     if (!strategy?.id) {
       toast({
         title: "Error",
         description: "No strategy ID available",
         variant: "destructive"
       });
       return;
     }

     try {
       switch (action) {
         case 'pause':
           await deploymentService.pauseStrategy(strategy.id);
           toast({
             title: "Strategy Paused",
             description: `Strategy "${strategy.name}" has been paused.`
           });
           break;
         case 'resume':
           await deploymentService.resumeStrategy(strategy.id);
           toast({
             title: "Strategy Resumed",
             description: `Strategy "${strategy.name}" has been resumed.`
           });
           break;
         case 'stop':
           await deploymentService.stopStrategy(strategy.id);
           toast({
             title: "Strategy Stopped",
             description: `Strategy "${strategy.name}" has been stopped.`
           });
           break;
       }
       
       // Refresh data after control action
       setTimeout(() => {
         loadDeploymentStatus(strategy.id!);
         fetchLivePositions(strategy.id!);
       }, 1000);
       
     } catch (error: any) {
       console.error(`Error ${action}ing strategy:`, error);
       toast({
         title: "Error",
         description: error.message || `Failed to ${action} strategy.`,
         variant: "destructive"
       });
     }
   };

   const handleRefreshData = () => {
     if (strategy?.id) {
       loadDeploymentStatus(strategy.id);
       fetchLivePositions(strategy.id);
       
       toast({
         title: "Data Refreshed",
         description: "Strategy data has been refreshed."
       });
     }
   };

  const getSymbols = (strategy: Strategy): Symbol[] => {
    if (!strategy.symbols && (!strategy.baskets || strategy.baskets.length === 0)) return [];
    
    // First, try to extract symbols from baskets
    if (strategy.baskets && Array.isArray(strategy.baskets) && strategy.baskets.length > 0) {
      const basketSymbols: Symbol[] = [];
      strategy.baskets.forEach(basket => {
        if (basket.symbols && Array.isArray(basket.symbols)) {
          basket.symbols.forEach(symbol => {
            basketSymbols.push({
              ticker: symbol.ticker || symbol.symbol || '',
              symbol: symbol.symbol || symbol.ticker || '',
              direction: symbol.direction || 'long',
              weight: symbol.weight || 1
            });
          });
        }
      });
      if (basketSymbols.length > 0) {
        return basketSymbols;
      }
    }
    
    // Fallback to regular symbols field
    if (strategy.symbols) {
    if (typeof strategy.symbols === 'string') {
      try {
        const symbolsArray = JSON.parse(strategy.symbols);
        if (Array.isArray(symbolsArray)) {
          return symbolsArray.map(s => ({
            ticker: s.symbol || s.name || '',
            symbol: s.symbol || s.name || '',
            direction: s.direction || 'long',
            weight: s.weight
          }));
        }
      } catch (e) {
        const symbolsString = strategy.symbols || '';
        return typeof symbolsString === 'string' ?
          symbolsString.split(',').map(s => ({
            ticker: s.trim(),
            symbol: s.trim(),
            direction: 'long' as const,
            weight: 1
          })) : [];
      }
    } else if (Array.isArray(strategy.symbols)) {
      return strategy.symbols.map(s => ({
        ticker: s.symbol || s.name || '',
        symbol: s.symbol || s.name || '',
        direction: s.direction || 'long',
        weight: s.weight
      }));
      }
    }
    
    return [];
  };

  const getDirectionIcon = (direction?: string) => {
    if (direction === 'short') {
      return <ArrowDown className="h-4 w-4 text-red-500" />;
    } else if (direction === 'auto') {
      return <ArrowUpDown className="h-4 w-4 text-purple-500" />;
    } else {
      return <ArrowUp className="h-4 w-4 text-green-500" />;
    }
  };

  const getSymbolName = (symbol: Symbol) => {
    return symbol.ticker || symbol.symbol || 'Unknown';
  };

  const getStrategyDuration = (strategy: Strategy) => {
    if (strategy.executionTiming?.signalFrequency) {
      return strategy.executionTiming.signalFrequency;
    }
    return strategy.duration || '1D';
  };

  return (
    <Dialog open={!!strategy} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[90vw]">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            {strategy?.name || 'Strategy Details'}
            {strategy?.isActive && (
              <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                <Check className="w-3 h-3" /> Active
              </div>
            )}
            {isLiveUpdatesActive && (
              <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                <RefreshCw className="w-3 h-3 animate-spin" /> Live Updates
              </div>
            )}
          </DialogTitle>
          <DialogDescription className="text-base pt-1 flex items-center justify-between">
            <span>{strategy?.description || 'Details for the selected trading strategy.'}</span>
            {isLiveUpdatesActive && (
              <span className="text-xs text-gray-500">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 max-h-[65vh] overflow-y-auto pr-2">
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg border">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
                <Settings className="w-5 h-5 text-blue-600" />
                Configuration
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Engine:</span>
                  <span className="font-medium">{getEngineDisplay(strategy)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Risk Level:</span>
                  <span className="font-medium capitalize">{strategy?.riskLevel || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Signal Frequency:</span>
                  <span className="font-medium">{getTimeframeDisplay(strategy)}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 pt-1">Symbols:</span>
                  <div className="flex flex-wrap gap-1 justify-end max-w-[70%]">
                    {renderSymbols()}
                                 </div>
             </div>

             {/* Strategy Control Buttons */}
             {deploymentStatus && (
               <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                 <h3 className="font-semibold mb-3 flex items-center gap-2 text-yellow-800">
                   <Settings className="w-5 h-5 text-yellow-600" />
                   Strategy Controls
                 </h3>
                 <div className="flex gap-2">
                   {deploymentStatus.status === 'running' && (
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => handleStrategyControl('pause')}
                       className="text-yellow-600 hover:text-yellow-700 border-yellow-300"
                     >
                       <Pause className="w-4 h-4 mr-2" />
                       Pause Strategy
                     </Button>
                   )}
                   
                   {deploymentStatus.status === 'paused' && (
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => handleStrategyControl('resume')}
                       className="text-green-600 hover:text-green-700 border-green-300"
                     >
                       <Play className="w-4 h-4 mr-2" />
                       Resume Strategy
                     </Button>
                   )}
                   
                   {(deploymentStatus.status === 'running' || deploymentStatus.status === 'paused') && (
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => handleStrategyControl('stop')}
                       className="text-red-600 hover:text-red-700 border-red-300"
                     >
                       <Square className="w-4 h-4 mr-2" />
                       Stop Strategy
                     </Button>
                   )}
                   
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => handleRefreshData()}
                     className="text-blue-600 hover:text-blue-700 border-blue-300"
                   >
                     <RefreshCw className="w-4 h-4 mr-2" />
                     Refresh Data
                   </Button>
                 </div>
               </div>
             )}
           </div>
         </div>

            {/* Strategy Parameters Section */}
            {(strategy?.params || strategy?.models?.length) && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-800">
                  <Calculator className="w-5 h-5 text-blue-600" />
                  Strategy Parameters
                </h3>
                <div className="space-y-3 text-sm">
                  {/* Custom Strategy Parameters (e.g., StewieQuant) */}
                  {strategy?.params && Object.keys(strategy.params).length > 0 && (
                    <div className="space-y-2">
                      <div className="font-medium text-blue-800 text-xs mb-2">Custom Configuration:</div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(strategy.params).map(([key, value]) => {
                          // Skip complex objects and arrays for display
                          if (typeof value === 'object' || key === 'aggregatedSymbols') return null;
                          
                          // Format parameter names for display
                          const displayKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                          
                          // Format values appropriately
                          let displayValue = value;
                          if (typeof value === 'number') {
                            if (key.includes('Threshold') || key.includes('Pct')) {
                              displayValue = key.includes('Pct') ? `${(value * 100).toFixed(1)}%` : value.toFixed(2);
                            } else if (key.includes('Lookback') || key.includes('HalfLife') || key.includes('DelayMs')) {
                              displayValue = `${value}${key.includes('DelayMs') ? 'ms' : ''}`;
                            } else {
                              displayValue = value.toFixed(2);
                            }
                          } else if (typeof value === 'boolean') {
                            displayValue = value ? 'Enabled' : 'Disabled';
                          }
                          
                          return (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-600">{displayKey}:</span>
                              <span className="font-medium">{displayValue as React.ReactNode}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Model Configurations */}
                  {strategy?.models && strategy.models.length > 0 && (
                    <div className="space-y-2">
                      <div className="font-medium text-blue-800 text-xs mb-2">AI Models ({strategy.models.length}):</div>
                      {strategy.models.map((model, index) => (
                        <div key={index} className="bg-white p-2 rounded border border-blue-200">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-blue-700">
                              {MODEL_TYPES.find(mt => mt.id === model.type)?.name || model.type}
                            </span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Weight: {(model.weight || 1).toFixed(1)}
                            </span>
                          </div>
                          {/* Display model-specific settings if they exist */}
                          {model.stewieQuantSettings && (
                            <div className="text-xs text-gray-600 mt-1">
                              Correlation: {model.stewieQuantSettings.correlationThreshold?.toFixed(2) || '0.7'}
                              • Z-Score: {model.stewieQuantSettings.zscoreThreshold?.toFixed(1) || '2.0'}
                              • Half-Life: {model.stewieQuantSettings.reversionHalfLife || '15'} days
                            </div>
                          )}
                          {model.lstmSettings && (
                            <div className="text-xs text-gray-600 mt-1">
                              Lookback: {model.lstmSettings.lookbackWindow || 'N/A'} 
                              • Layers: {model.lstmSettings.numLayers || 'N/A'}
                              • Units: {model.lstmSettings.numUnits || 'N/A'}
                            </div>
                          )}
                          {model.arimaSettings && (
                            <div className="text-xs text-gray-600 mt-1">
                              Order: ({model.arimaSettings.p || 1},{model.arimaSettings.d || 1},{model.arimaSettings.q || 1})
                              {model.arimaSettings.seasonal ? ' • Seasonal' : ' • Non-seasonal'}
                            </div>
                          )}
                          {model.xgboostSettings && (
                            <div className="text-xs text-gray-600 mt-1">
                              Estimators: {model.xgboostSettings.n_estimators || 'N/A'}
                              • Max Depth: {model.xgboostSettings.max_depth || 'N/A'}
                              • Learning Rate: {model.xgboostSettings.learning_rate || 'N/A'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-orange-800">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
                Risk Management
                {deploymentStatus?.strategy_config?.risk_params && (
                  <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 border-orange-300">
                    VM Config
                  </Badge>
                )}
              </h3>
              <div className="space-y-2 text-sm">
                {(() => {
                  // Prioritize VM deployment risk params, fallback to strategy data
                  const vmRiskParams = deploymentStatus?.strategy_config?.risk_params;
                  const strategyRiskParams = strategy?.riskParameters;
                  const riskParams = vmRiskParams || strategyRiskParams;
                  
                  if (!riskParams) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-orange-600 text-sm mb-2">No risk management configuration found</p>
                        <p className="text-xs text-orange-500">
                          {deploymentStatus ? 'VM deployment has no risk params configured' : 'Strategy has no risk management settings'}
                        </p>
                      </div>
                    );
                  }
                  
                  return (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Dynamic AI Exits:</span>
                        <span className={`font-medium ${
                          riskParams.allow_dynamic_exits || riskParams.allowDynamicExits ? 'text-green-600' : 'text-gray-700'
                        }`}>
                          {riskParams.allow_dynamic_exits || riskParams.allowDynamicExits ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      
                      {/* Show profit target and stop loss if dynamic exits are disabled */}
                      {!(riskParams.allow_dynamic_exits || riskParams.allowDynamicExits) && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">
                              Profit Target ({riskParams.profit_target_type || riskParams.profitTargetType || 'Percentage'}):
                            </span>
                            <span className="font-medium text-green-600">
                              {(() => {
                                const value = riskParams.profit_target || riskParams.profitTargetValue;
                                const type = riskParams.profit_target_type || riskParams.profitTargetType;
                                if (value !== undefined && value !== null) {
                                  return type === 'percentage' || !type ? `${value}%` : `$${value}`;
                                }
                                return 'N/A';
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">
                              Stop Loss ({riskParams.stop_loss_type || riskParams.stopLossType || 'Percentage'}):
                            </span>
                            <span className="font-medium text-red-600">
                              {(() => {
                                const value = riskParams.stop_loss || riskParams.stopLossValue;
                                const type = riskParams.stop_loss_type || riskParams.stopLossType;
                                if (value !== undefined && value !== null) {
                                  return type === 'percentage' || !type ? `${value}%` : `$${value}`;
                                }
                                return 'N/A';
                              })()}
                            </span>
                          </div>
                        </>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Max Position Size:</span>
                        <span className="font-medium">
                          {(() => {
                            const maxPosition = riskParams.max_position_size || riskParams.maxPositionSizePct;
                            if (maxPosition !== undefined && maxPosition !== null) {
                              // Convert to percentage if it's a decimal
                              const percentage = typeof maxPosition === 'number' && maxPosition <= 1 
                                ? maxPosition * 100 
                                : maxPosition;
                              return `${parseFloat(percentage).toFixed(1)}%`;
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Max Strategy Exposure:</span>
                        <span className="font-medium">
                          {(() => {
                            const exposure = riskParams.max_strategy_exposure || riskParams.maxStrategyExposurePct;
                            if (exposure !== undefined && exposure !== null) {
                              return `${parseFloat(exposure).toFixed(1)}%`;
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Volatility Weighted:</span>
                        <span className="font-medium">
                          {riskParams.is_vol_weighted_risk || riskParams.isVolWeightedRisk ? 'Yes' : 'No'}
                        </span>
                      </div>
                      
                      {/* Additional VM-specific risk parameters */}
                      {vmRiskParams && (
                        <>
                          {vmRiskParams.max_drawdown && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Max Drawdown:</span>
                              <span className="font-medium text-red-600">
                                {vmRiskParams.max_drawdown}%
                              </span>
                            </div>
                          )}
                          {vmRiskParams.risk_free_rate !== undefined && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Risk Free Rate:</span>
                              <span className="font-medium">
                                {vmRiskParams.risk_free_rate}%
                              </span>
                            </div>
                          )}
                          {vmRiskParams.correlation_threshold !== undefined && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Correlation Threshold:</span>
                              <span className="font-medium">
                                {vmRiskParams.correlation_threshold}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Data source indicator */}
                      <div className="mt-3 pt-2 border-t border-orange-200">
                        <div className="text-orange-600 text-xs">
                          {vmRiskParams ? 
                            '✓ Risk parameters from VM deployment' : 
                            '⚠ Using local strategy risk settings'
                          }
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Performance Metrics Section */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-green-800">
                <BarChart className="w-5 h-5 text-green-600" />
                Performance Metrics
              </h3>
                             <div className="space-y-3 text-sm">
                 {/* Real-time Performance */}
                 {deploymentStatus && (
                   <div className="bg-white p-3 rounded border border-green-200">
                     <h4 className="font-medium text-green-800 mb-2 flex items-center gap-1">
                       <Activity className="w-4 h-4" />
                       Live Performance
                     </h4>
                     <div className="grid grid-cols-2 gap-4 text-xs">
                       <div>
                         <div className="flex justify-between mb-1">
                           <span className="text-gray-600">Total P&L:</span>
                           <span className={`font-medium ${deploymentStatus.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                             {deploymentStatus.total_pnl >= 0 ? '+' : ''}${deploymentStatus.total_pnl.toFixed(2)}
                           </span>
                         </div>
                         <div className="flex justify-between mb-1">
                           <span className="text-gray-600">Active Positions:</span>
                           <span className="font-medium">{deploymentStatus.active_positions}</span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600">Trading Mode:</span>
                           <span className="font-medium">
                             {deploymentStatus.strategy_config?.trading_mode === 'PAPER' ? 'Paper' : 'Live'}
                           </span>
                         </div>
                       </div>
                       <div>
                         <div className="flex justify-between mb-1">
                           <span className="text-gray-600">Strategy Status:</span>
                           <span className={`font-medium capitalize ${
                             deploymentStatus.status === 'running' ? 'text-green-600' : 
                             deploymentStatus.status === 'paused' ? 'text-yellow-600' : 
                             'text-gray-600'
                           }`}>
                             {deploymentStatus.status}
                           </span>
                         </div>
                         <div className="flex justify-between mb-1">
                           <span className="text-gray-600">Symbols Trading:</span>
                           <span className="font-medium">{deploymentStatus.strategy_config?.symbols?.length || 0}</span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600">Portfolio Allocation:</span>
                           <span className="font-medium">{deploymentStatus.strategy_config?.baskets?.length || 0} Baskets</span>
                         </div>
                       </div>
                     </div>
                   </div>
                 )}

                {/* Position Summary */}
                {livePositions.length > 0 && (
                  <div className="bg-white p-3 rounded border border-green-200">
                    <h4 className="font-medium text-green-800 mb-2 flex items-center gap-1">
                      <Calculator className="w-4 h-4" />
                      Position Summary
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600">Total Market Value:</span>
                          <span className="font-medium">
                            ${livePositions.reduce((sum, pos) => sum + pos.market_value, 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600">Total Cost Basis:</span>
                          <span className="font-medium">
                            ${livePositions.reduce((sum, pos) => sum + pos.cost_basis, 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600">Unrealized P&L:</span>
                          <span className={`font-medium ${
                            livePositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            ${livePositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600">Avg. P&L %:</span>
                          <span className={`font-medium ${
                            (livePositions.reduce((sum, pos) => sum + pos.unrealized_plpc, 0) / livePositions.length) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {(livePositions.reduce((sum, pos) => sum + pos.unrealized_plpc, 0) / livePositions.length).toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                                 {/* Fallback for non-deployed strategies */}
                 {!deploymentStatus && !livePositions.length && (
                   <div className="bg-blue-50 border border-blue-200 rounded p-3">
                     <p className="text-blue-800 font-medium mb-2">📊 Strategy Not Currently Deployed</p>
                     <p className="text-blue-700 text-xs leading-relaxed">
                       This strategy is not actively trading. Deploy it to see live performance metrics 
                       and position data. For historical performance, check the backtest results.
                     </p>
                   </div>
                 )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
                <Activity className="w-5 h-5 text-indigo-600" />
                Recent Trading Activity
              </h3>
              <div className="space-y-2 text-sm">
                {activityLoading && <p className="text-gray-500">Loading recent trades...</p>}
                {activityError && <p className="text-red-500">Error: {activityError}</p>}
                {!activityLoading && !activityError && activities.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-gray-500 mb-2">No recent trading activity found.</p>
                    <p className="text-xs text-gray-400">
                      {deploymentStatus?.status === 'running' 
                        ? 'Strategy is running but no trades have been executed yet.' 
                        : deploymentStatus?.status === 'paused'
                        ? 'Strategy is paused. Resume to see new activity.'
                        : 'Strategy may not be actively trading.'}
                    </p>
                  </div>
                )}
                {!activityLoading && !activityError && activities.length > 0 && (
                  <div className="max-h-60 overflow-y-auto">
                    {activities.slice(0, 10).map(act => (
                      <div key={act.id} className="p-3 border rounded-lg mb-2 bg-white hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold px-2 py-1 rounded text-xs ${
                                act.action === 'buy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {act.action.toUpperCase()}
                              </span>
                              <span className="font-medium text-gray-900">{act.symbol}</span>
                              <Badge variant="outline" className="text-xs">
                                {act.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-600 space-x-3">
                              <span>Qty: <strong>{act.quantity}</strong></span>
                              <span>Price: <strong>${act.price?.toFixed(2)}</strong></span>
                              <span>Value: <strong>${(act.quantity * act.price).toFixed(2)}</strong></span>
                              {act.order_type && <span>Type: <strong className="capitalize">{act.order_type}</strong></span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              {act.timestamp ? format(new Date(act.timestamp), 'MMM d') : 'No date'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {act.timestamp ? format(new Date(act.timestamp), 'HH:mm') : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {activities.length > 10 && (
                      <div className="text-center pt-2">
                        <p className="text-xs text-gray-500">
                          Showing latest 10 of {activities.length} activities
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

                         {/* Live Trading Section */}
             <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
               <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-800">
                 <Play className="w-5 h-5 text-blue-600" />
                 Live Trading
                 {deploymentStatus && (
                   <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                     VM Deployed
                   </Badge>
                 )}
               </h3>
               <div className="space-y-2 text-sm">
                 {deploymentStatus ? (
                   <>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Deployment Status:</span>
                       <Badge variant={deploymentStatus.status === 'running' ? 'default' : deploymentStatus.status === 'paused' ? 'secondary' : 'outline'}>
                         {deploymentStatus.status === 'running' ? 'Running' : deploymentStatus.status === 'paused' ? 'Paused' : deploymentStatus.status === 'stopped' ? 'Stopped' : deploymentStatus.status || 'Unknown'}
                       </Badge>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Trading Mode:</span>
                       <Badge variant={deploymentStatus.strategy_config?.trading_mode === 'PAPER' ? 'default' : 'outline'}>
                         {deploymentStatus.strategy_config?.trading_mode === 'PAPER' ? 'Paper Trading' : 'Live Trading'}
                       </Badge>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Active Positions:</span>
                       <span className="font-medium">{livePositions.length}</span>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Total P&L:</span>
                       <span className={`font-medium ${
                         livePositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0) >= 0 ? 'text-green-600' : 'text-red-600'
                       }`}>
                         ${livePositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0).toFixed(2)}
                       </span>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Symbols in Strategy:</span>
                       <span className="font-medium">{deploymentStatus.strategy_config?.symbols?.length || 0}</span>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Baskets in Strategy:</span>
                       <span className="font-medium">{deploymentStatus.strategy_config?.baskets?.length || 0}</span>
                     </div>
                     <div className="flex justify-between items-center">
                       <span className="text-gray-600">Last Updated:</span>
                       <span className="font-medium">{deploymentStatus.deployment_time ? format(new Date(deploymentStatus.deployment_time), 'MMM d, yyyy HH:mm') : 'N/A'}</span>
                     </div>
                   </>
                 ) : (
                   <div className="text-center py-4">
                     <p className="text-blue-600 text-sm mb-2">Strategy Not Currently Deployed</p>
                     <p className="text-xs text-blue-500">
                       This strategy is not actively deployed on the VM. Deploy it to see live trading data.
                     </p>
                   </div>
                 )}
               </div>
             </div>

            {/* Live Positions Table */}
            <div className="bg-gray-50 p-4 rounded-lg border">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
                <Square className="w-5 h-5 text-indigo-600" />
                Live Positions
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Market Value
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unrealized P&L
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unrealized P&L %
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost Basis
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingLivePositions && <tr><td colSpan={7} className="text-center py-4">Loading positions...</td></tr>}
                    {livePositionsError && <tr><td colSpan={7} className="text-center py-4 text-red-500">{livePositionsError}</td></tr>}
                    {!isLoadingLivePositions && !livePositionsError && livePositions.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-4 text-gray-500">No live positions found for this strategy.</td></tr>
                    )}
                    {!isLoadingLivePositions && !livePositionsError && livePositions.length > 0 && (
                      livePositions.map((pos, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {getSymbolName({ ticker: pos.symbol, symbol: pos.symbol, direction: 'long', weight: 1 })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {pos.qty}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${pos.current_price?.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${pos.market_value?.toFixed(2)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${pos.unrealized_pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${pos.unrealized_pl?.toFixed(2)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${pos.unrealized_plpc >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {pos.unrealized_plpc?.toFixed(2)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${pos.cost_basis?.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t flex items-center justify-between">
          <div className="flex items-center gap-2">
            {deploymentStatus && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  // Navigate to trading history page filtered by strategy
                  window.location.href = `/trading-history?strategy=${strategy?.id}`;
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                <Activity className="w-4 h-4 mr-2" />
                View Full History
              </Button>
            )}
          </div>
          <DialogClose asChild>
            <Button variant="outline" className="rounded-lg">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
